import { Checkbox, Form, Input, Radio, Row, Space } from 'antd';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import React, { useEffect, useState } from 'react';
import { ISubj } from 'types/amlz';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import TranslateText from 'components/hoc/TranslateText';

import { IcisStaty } from 'services/cisStaty';

import { EAnoNe } from 'enums/anoNe';

import anoNeSelect from 'data/codebooks/anoNeSelect';

import pscValidation from 'utils/validations/pscValidation';

interface IC5PridatOsobu {
    personData: ISubj | undefined;
    dotaznikOnly: boolean;
    setPersonData: (value: ISubj | undefined) => void;
    setSubmitPersonData: (value: ISubj | null) => void;
    countries?: IcisStaty[];
}

const C5PridatOsobu: React.FC<IC5PridatOsobu> = ({
    personData,
    setSubmitPersonData,
    setPersonData,
    countries,
    dotaznikOnly
}) => {
    const [form] = Form.useForm();
    const [showDotaznik, setShowDotaznik] = useState<boolean>(false);
    const [submitDataOsoba, setSubmitDataOsoba] = useState<ISubj | undefined>(personData);
    const [checkBoxesDisabled, setCheckBoxesDisabled] = useState<boolean>(false);
    const [, setRerender] = useState<boolean>(false);

    useEffect(() => {
        dotaznikOnly && setShowDotaznik(true);
        typeof personData?.dotaznikpep?.zrizovatelmaj === 'boolean' &&
            form.setFieldsValue({
                zrizovatelmaj: personData?.dotaznikpep?.zrizovatelmaj
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        if (!showDotaznik) {
            setSubmitDataOsoba({
                adma: fieldsValue.adma,
                jmeno: fieldsValue.jmeno,
                kost: fieldsValue.kost,
                mema: fieldsValue.mema,
                osobasankceje: fieldsValue.osobasankceje === EAnoNe.ANO,
                prijmeni: fieldsValue.prijmeni,
                pscm: fieldsValue.pscm,
                statlegalizaceje: fieldsValue.statlegalizaceje === EAnoNe.ANO,
                ...(personData?.c2osobaoid && {
                    c2osobaoid: personData.c2osobaoid
                })
            });
            setShowDotaznik(true);
        } else {
            setSubmitPersonData({
                ...submitDataOsoba,
                dotaznikpep: {
                    ...fieldsValue
                }
            });
        }
    };

    const checkBoxValues = [
        'VZ',
        'PM',
        'PO',
        'VL',
        'DP',
        'IF',
        'SJ',
        'DE',
        'VN',
        'SD',
        'SG',
        'SF',
        'PI',
        'CZ',
        'ZA',
        'PP',
        'JI'
    ];

    useEffect(() => {
        setTimeout(() => {
            setCheckBoxesDisabled((form.getFieldValue('puvodmajetku') || []).length > 2);
        }, 0);
    });

    const renderCheckBoxes = () => {
        return (
            <Form.Item
                style={{
                    marginBottom: 8
                }}
                name="puvodmajetku"
                initialValue={personData?.dotaznikpep?.puvodmajetku}
                label={<Paragraph text={'DOTAZNIK_PEP_PUVODMAJETKU'} strong={true} />}
                rules={[
                    {
                        required: true,
                        message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                    }
                ]}
            >
                <Checkbox.Group
                    style={{
                        width: '100%',
                        marginBottom: 26
                    }}
                    onChange={() => setRerender(pv => !pv)}
                >
                    <Space direction="vertical">
                        {checkBoxValues.map((e, i) =>
                            e === 'JI' ? (
                                <Row key={`${e}${i}`}>
                                    <Checkbox
                                        value="JI"
                                        disabled={
                                            checkBoxesDisabled &&
                                            !(form.getFieldValue('puvodmajetku') || []).includes('JI')
                                        }
                                    >
                                        <TranslateText>DOTAZNIK_PEP_PUVODMAJETKU_RADIO17</TranslateText>{' '}
                                        {(form.getFieldValue('puvodmajetku') || []).includes('JI') ? (
                                            <Form.Item
                                                noStyle={true}
                                                name="puvodmajetkujine"
                                                initialValue={personData?.dotaznikpep?.puvodmajetkujine}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: (
                                                            <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                                                        )
                                                    }
                                                ]}
                                            >
                                                <Input
                                                    style={{
                                                        width: '400px',
                                                        marginLeft: 10
                                                    }}
                                                />
                                            </Form.Item>
                                        ) : null}
                                    </Checkbox>
                                </Row>
                            ) : (
                                <Checkbox
                                    value={e}
                                    disabled={
                                        checkBoxesDisabled && !(form.getFieldValue('puvodmajetku') || []).includes(e)
                                    }
                                >
                                    <TranslateText>{`DOTAZNIK_PEP_PUVODMAJETKU_RADIO${i + 1}`}</TranslateText>
                                </Checkbox>
                            )
                        )}
                    </Space>
                </Checkbox.Group>
            </Form.Item>
        );
    };

    return (
        <Row justify="start">
            <Form
                preserve={false}
                scrollToFirstError={true}
                form={form}
                layout="vertical"
                initialValues={{
                    remember: true
                }}
                onFinish={onFinish}
                onFinishFailed={(err: ValidateErrorEntity) => console.log('chyba:', err)}
                name="C5PridatOsobu"
                style={{
                    width: '100%'
                }}
            >
                {!showDotaznik ? (
                    <>
                        <Title level={5} dividerTitle={true} text="OSOBA" />
                        <InputComponent
                            label="FORMULARE_JMENA"
                            name="jmeno"
                            initialValue={personData?.jmeno}
                            type="text"
                            maxLength={100}
                        />
                        <InputComponent
                            label="FORMULARE_PRIJMENI"
                            name="prijmeni"
                            initialValue={personData?.prijmeni}
                            type="text"
                            maxLength={100}
                        />
                        <Title level={5} dividerTitle={true} text="FORMULARE_ADRESA_POBYTU" />
                        <InputComponent
                            label="ULICE_CP"
                            name="adma"
                            initialValue={personData?.adma}
                            type="text"
                            maxLength={50}
                        />
                        <InputComponent
                            label="MESTO"
                            name="mema"
                            initialValue={personData?.mema}
                            type="text"
                            maxLength={50}
                        />
                        <InputComponent
                            label="PSC"
                            name="pscm"
                            initialValue={personData?.pscm}
                            rules={pscValidation('kost')}
                            type="text"
                            maxLength={10}
                        />
                        <SelectComponent
                            isFormItem={true}
                            name="kost"
                            labelParagraph="STAT"
                            defaultValue={personData?.kost}
                            data={countries ? countries : []}
                        />
                        <Title level={5} dividerTitle={true} text="DOPLNUJICI_INFORMACE" />
                        <SelectComponent
                            isFormItem={true}
                            name="osobasankceje"
                            labelParagraph="FORMULARE_AMLZ_C5_SELECT_DOPLN_INFO1"
                            defaultValue={
                                typeof personData?.osobasankceje === 'boolean'
                                    ? personData?.osobasankceje
                                        ? EAnoNe.ANO
                                        : EAnoNe.NE
                                    : undefined
                            }
                            data={anoNeSelect}
                        />
                        <SelectComponent
                            name="statlegalizaceje"
                            isFormItem={true}
                            labelParagraph="FORMULARE_AMLZ_C5_SELECT_DOPLN_INFO2"
                            defaultValue={
                                typeof personData?.statlegalizaceje === 'boolean'
                                    ? personData?.statlegalizaceje
                                        ? EAnoNe.ANO
                                        : EAnoNe.NE
                                    : undefined
                            }
                            data={anoNeSelect}
                        />{' '}
                    </>
                ) : (
                    <>
                        <Title level={5} dividerTitle={true} text="DOTAZNIK_PEP" />
                        <Paragraph text={'DOTAZNIK_PEP_TEXT'} />
                        <Paragraph text={'DOTAZNIK_PEP_TEXT2'} strong={true} />

                        <InputTextArea
                            rows={4}
                            name="funkce"
                            initialValue={personData?.dotaznikpep?.funkce}
                            label="DOTAZNIK_PEP_FUNKCE"
                            maxLength={2500}
                        />
                        <Form.Item
                            style={{
                                marginBottom: 8
                            }}
                            name="zrizovatelmaj"
                            initialValue={personData?.dotaznikpep?.zrizovatelmaj}
                            label={<Paragraph text={'DOTAZNIK_PEP_ZRIZOVATELMAJ'} strong={true} />}
                            rules={[
                                {
                                    required: true,
                                    message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                                }
                            ]}
                        >
                            <Radio.Group
                                style={{
                                    marginBottom: 26
                                }}
                                onChange={() => setRerender(pv => !pv)}
                            >
                                <Radio value={true}>
                                    <TranslateText>ANO</TranslateText>
                                </Radio>
                                <Radio value={false}>
                                    <TranslateText>NE</TranslateText>
                                </Radio>
                            </Radio.Group>
                        </Form.Item>
                        {typeof form.getFieldValue('zrizovatelmaj') === 'boolean' &&
                        !form.getFieldValue('zrizovatelmaj') ? (
                            <>
                                {renderCheckBoxes()}
                                <Form.Item
                                    style={{
                                        marginBottom: 8
                                    }}
                                    name="rozsahmajetku"
                                    initialValue={personData?.dotaznikpep?.rozsahmajetku}
                                    label={<Paragraph text={'DOTAZNIK_PEP_ROZSAHMAJETKU'} strong={true} />}
                                    rules={[
                                        {
                                            required: true,
                                            message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                                        }
                                    ]}
                                >
                                    <Radio.Group
                                        style={{
                                            marginBottom: 26
                                        }}
                                    >
                                        <Space direction="vertical">
                                            <Radio value={'DO5'}>
                                                <TranslateText>DOTAZNIK_PEP_ROZSAHMAJETKU_RADIO1</TranslateText>
                                            </Radio>
                                            <Radio value={'DO10'}>
                                                <TranslateText>DOTAZNIK_PEP_ROZSAHMAJETKU_RADIO2</TranslateText>
                                            </Radio>
                                            <Radio value={'DO50'}>
                                                <TranslateText>DOTAZNIK_PEP_ROZSAHMAJETKU_RADIO3</TranslateText>
                                            </Radio>
                                            <Radio value={'NAD50'}>
                                                <TranslateText>DOTAZNIK_PEP_ROZSAHMAJETKU_RADIO4</TranslateText>
                                            </Radio>
                                        </Space>
                                    </Radio.Group>
                                </Form.Item>
                            </>
                        ) : null}
                        <Form.Item
                            style={{
                                marginBottom: 8
                            }}
                            name="majetkovepomery"
                            initialValue={personData?.dotaznikpep?.majetkovepomery}
                            label={<Paragraph text={'DOTAZNIK_PEP_MAJETKOVEPOMERY'} strong={true} />}
                            rules={[
                                {
                                    required: true,
                                    message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                                }
                            ]}
                        >
                            <Radio.Group
                                style={{
                                    marginBottom: 26
                                }}
                            >
                                <Radio value={true}>
                                    <TranslateText>ANO</TranslateText>
                                </Radio>
                                <Radio value={false}>
                                    <TranslateText>NE</TranslateText>
                                </Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item
                            style={{
                                marginBottom: 8
                            }}
                            name="prevzdrojmajetku"
                            initialValue={personData?.dotaznikpep?.prevzdrojmajetku}
                            label={<Paragraph text={'DOTAZNIK_PEP_PREVZDROJMAJETKU'} strong={true} />}
                            rules={[
                                {
                                    required: true,
                                    message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                                }
                            ]}
                        >
                            <Radio.Group
                                style={{
                                    marginBottom: 26
                                }}
                            >
                                <Space direction="vertical">
                                    <Radio value={'VC'}>
                                        <TranslateText>DOTAZNIK_PEP_PREVZDROJMAJETKU_RADIO1</TranslateText>
                                    </Radio>
                                    <Radio value={'CZ'}>
                                        <TranslateText>DOTAZNIK_PEP_PREVZDROJMAJETKU_RADIO2</TranslateText>
                                    </Radio>
                                </Space>
                            </Radio.Group>
                        </Form.Item>
                        <InputTextArea
                            rows={4}
                            name="predpvysemajetku"
                            initialValue={personData?.dotaznikpep?.predpvysemajetku}
                            label="DOTAZNIK_PEP_PREDPVYSEMAJETKU"
                            maxLength={2500}
                        />
                    </>
                )}
                <Row className="button-row">
                    <ButtonComponent
                        buttontext="ZPET"
                        id="C5ZpetBtn"
                        type="default"
                        onClick={() => {
                            showDotaznik && !dotaznikOnly
                                ? setShowDotaznik(false)
                                : (setSubmitPersonData(null), setPersonData(undefined));
                        }}
                    />
                    <ButtonComponent
                        buttontext="POKRACOVAT"
                        id="C5PokracovatBtn"
                        type="primary"
                        onClick={() => form.submit()}
                    />
                </Row>
            </Form>
        </Row>
    );
};

export default C5PridatOsobu;
